import { useEffect, useMemo, useState, type FormEvent } from "react";
import { api } from "../api";
import type { FeatureList, HostPlan, PanelUser, Role } from "../types";

/** Resource value: null = unlimited */
export type ResourceValue = number | null;

/** Minimal plan shape for resource defaults (Hosting Plan) */
export type PlanResourceSource = {
  id: string;
  name: string;
  diskMb: number;
  maxDatabases: number;
  maxEmails: number;
  maxFtpAccounts: number;
  maxDomains: number;
  maxSubdomains: number;
  maxAddonDomains?: number;
  bandwidthMb: number;
  isDefault?: boolean;
  planTag?: string;
  maxRamMb?: number;
  maxCpuCores?: number;
  featureListId?: string | null;
};

export type CreateAccountPayload = {
  username: string;
  password: string;
  email: string;
  role: Role;
  /** Hosting Plan id (resources) */
  planId: string;
  /** @deprecated alias of planId for API compatibility */
  packageId: string;
  domain?: string;
  sshAccess?: boolean;
  sftpOnly?: boolean;
  denyCron?: boolean;
  createDefaultEmail?: boolean;
  parentResellerId?: string | null;
  featureListId?: string | null;
  resources?: {
    diskMb?: number | null;
    maxInodes?: number | null;
    bandwidthMb?: number | null;
    maxFtpAccounts?: number | null;
    maxEmails?: number | null;
    maxEmailQuotaMb?: number | null;
    maxDatabases?: number | null;
    maxSubdomains?: number | null;
    maxParkedDomains?: number | null;
    maxAddonDomains?: number | null;
    maxDomains?: number | null;
    maxHourlyEmail?: number | null;
    maxFailPercent?: number | null;
  };
};

type Resources = {
  diskMb: ResourceValue;
  maxInodes: ResourceValue;
  bandwidthMb: ResourceValue;
  maxFtpAccounts: ResourceValue;
  maxEmails: ResourceValue;
  maxEmailQuotaMb: ResourceValue;
  maxDatabases: ResourceValue;
  maxSubdomains: ResourceValue;
  maxParkedDomains: ResourceValue;
  maxAddonDomains: ResourceValue;
  maxDomains: ResourceValue;
  maxHourlyEmail: ResourceValue;
  maxFailPercent: ResourceValue;
};

const UNLIMITED: ResourceValue = null;

export type AccountResources = Resources;

export function emptyResources(): Resources {
  return {
    diskMb: UNLIMITED,
    maxInodes: UNLIMITED,
    bandwidthMb: UNLIMITED,
    maxFtpAccounts: UNLIMITED,
    maxEmails: UNLIMITED,
    maxEmailQuotaMb: UNLIMITED,
    maxDatabases: UNLIMITED,
    maxSubdomains: UNLIMITED,
    maxParkedDomains: UNLIMITED,
    maxAddonDomains: UNLIMITED,
    maxDomains: UNLIMITED,
    maxHourlyEmail: UNLIMITED,
    maxFailPercent: UNLIMITED
  };
}

export function fromPackage(p: PlanResourceSource | undefined): Resources {
  if (!p) return emptyResources();
  return {
    diskMb: p.diskMb > 0 ? p.diskMb : UNLIMITED,
    maxInodes: p.diskMb > 0 ? p.diskMb * 20 : UNLIMITED,
    bandwidthMb: p.bandwidthMb > 0 ? p.bandwidthMb : UNLIMITED,
    maxFtpAccounts: p.maxFtpAccounts > 0 ? p.maxFtpAccounts : UNLIMITED,
    maxEmails: p.maxEmails > 0 ? p.maxEmails : UNLIMITED,
    maxEmailQuotaMb: 1024,
    maxDatabases: p.maxDatabases > 0 ? p.maxDatabases : UNLIMITED,
    maxSubdomains: p.maxSubdomains > 0 ? p.maxSubdomains : UNLIMITED,
    maxParkedDomains: UNLIMITED,
    maxAddonDomains:
      (p.maxAddonDomains ?? 0) > 0 ? p.maxAddonDomains! : UNLIMITED,
    maxDomains: p.maxDomains > 0 ? p.maxDomains : UNLIMITED,
    maxHourlyEmail: UNLIMITED,
    maxFailPercent: UNLIMITED
  };
}

/** Merge plan + account overrides for modify form */
export function resourcesFromAccount(
  account: PanelUser,
  pkg: PlanResourceSource | undefined
): Resources {
  const base = fromPackage(pkg);
  const o = account.limitsOverride || {};
  const pick = (
    override: number | null | undefined,
    fallback: ResourceValue
  ): ResourceValue => {
    if (override !== undefined) {
      if (override === null || override === 0) return null;
      return override;
    }
    return fallback;
  };
  return {
    diskMb: pick(o.diskMb ?? account.quotaDiskMb ?? undefined, base.diskMb),
    maxInodes: pick(
      o.maxInodes ?? account.quotaInodes ?? undefined,
      base.maxInodes
    ),
    bandwidthMb: pick(o.bandwidthMb, base.bandwidthMb),
    maxFtpAccounts: pick(o.maxFtpAccounts, base.maxFtpAccounts),
    maxEmails: pick(o.maxEmails, base.maxEmails),
    maxEmailQuotaMb: pick(o.maxEmailQuotaMb, base.maxEmailQuotaMb),
    maxDatabases: pick(o.maxDatabases, base.maxDatabases),
    maxSubdomains: pick(o.maxSubdomains, base.maxSubdomains),
    maxParkedDomains: pick(o.maxParkedDomains, base.maxParkedDomains),
    maxAddonDomains: pick(o.maxAddonDomains, base.maxAddonDomains),
    maxDomains: pick(o.maxDomains, base.maxDomains),
    maxHourlyEmail: pick(o.maxHourlyEmail, base.maxHourlyEmail),
    maxFailPercent: pick(o.maxFailPercent, base.maxFailPercent)
  };
}

function passwordStrength(pw: string): number {
  if (!pw) return 0;
  let s = 0;
  if (pw.length >= 8) s += 25;
  if (pw.length >= 12) s += 15;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) s += 20;
  if (/\d/.test(pw)) s += 20;
  if (/[^a-zA-Z0-9]/.test(pw)) s += 20;
  return Math.min(100, s);
}

/**
 * Webuzo-style Add New User form — plan, unlimited/custom resources, settings.
 */
export function CreateAccountForm({
  plans,
  packages,
  accounts,
  currentUser,
  busy,
  onSubmit,
  onCancel
}: {
  /** Hosting Plans (resources) — preferred */
  plans?: HostPlan[] | PlanResourceSource[];
  /** @deprecated use plans — kept for call-site compat */
  packages?: PlanResourceSource[];
  accounts: PanelUser[];
  currentUser: PanelUser;
  busy: boolean;
  onSubmit: (
    data: CreateAccountPayload
  ) => Promise<{ setupError?: string; domainError?: string } | void>;
  onCancel: () => void;
}) {
  const staff =
    currentUser.role === "superadmin" || currentUser.role === "admin";
  const reseller = currentUser.role === "reseller";
  const hostingPlans = useMemo(
    () => (plans && plans.length ? plans : packages || []) as PlanResourceSource[],
    [plans, packages]
  );
  const defaultPlan =
    hostingPlans.find((p) => p.isDefault) || hostingPlans[0];

  const [username, setUsername] = useState("");
  const [domain, setDomain] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [planId, setPlanId] = useState(defaultPlan?.id || "");
  const [manual, setManual] = useState(true);
  const [resources, setResources] = useState<Resources>(() =>
    fromPackage(defaultPlan)
  );
  const [sshAccess, setSshAccess] = useState(false);
  const [denyCron, setDenyCron] = useState(false);
  const [dedicatedIpv4, setDedicatedIpv4] = useState(false);
  const [dedicatedIpv6, setDedicatedIpv6] = useState(false);
  const [locale, setLocale] = useState("en");
  const [theme, setTheme] = useState("default");
  const [ownerId, setOwnerId] = useState("root");
  const [demoAccount, setDemoAccount] = useState(false);
  const [createDefaultEmail, setCreateDefaultEmail] = useState(true);
  const [featureListId, setFeatureListId] = useState("");
  const [featureLists, setFeatureLists] = useState<FeatureList[]>([]);
  const [error, setError] = useState("");
  const [localBusy, setLocalBusy] = useState(false);
  const [step, setStep] = useState(0); // 0 Account, 1 Plan, 2 Resources, 3 Settings

  const resellers = useMemo(
    () => accounts.filter((a) => a.role === "reseller"),
    [accounts]
  );

  useEffect(() => {
    if (!staff) return;
    void api<{ items: FeatureList[] }>("/api/v1/feature-lists")
      .then((r) => {
        setFeatureLists(r.items || []);
        const def = r.items?.find((f) => f.isDefault) || r.items?.[0];
        if (def) setFeatureListId(def.id);
      })
      .catch(() => setFeatureLists([]));
  }, [staff]);

  useEffect(() => {
    if (!manual && planId) {
      const p = hostingPlans.find((x) => x.id === planId);
      setResources(fromPackage(p));
    }
  }, [planId, manual, hostingPlans]);

  const strength = passwordStrength(password);

  const suggestUserFromDomain = (d: string) => {
    const clean = d
      .toLowerCase()
      .replace(/^www\./, "")
      .replace(/\.[a-z]{2,}$/i, "")
      .replace(/[^a-z0-9]/g, "")
      .slice(0, 8);
    if (clean && !username) setUsername(clean);
  };

  const generatePassword = () => {
    const chars =
      "abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!@#$%";
    let pw = "";
    const arr = new Uint32Array(16);
    crypto.getRandomValues(arr);
    for (let i = 0; i < 16; i++) pw += chars[arr[i]! % chars.length];
    setPassword(pw);
    setPassword2(pw);
    setShowPw(true);
  };

  const setRes = (key: keyof Resources, v: ResourceValue) =>
    setResources((r) => ({ ...r, [key]: v }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    if (password !== password2) {
      setError("Passwords do not match");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (!planId) {
      setError("Choose a hosting plan");
      return;
    }
    setLocalBusy(true);
    try {
      await onSubmit({
        username,
        password,
        email,
        role: "user",
        planId,
        packageId: planId,
        domain: domain.trim() || undefined,
        sshAccess,
        sftpOnly: !sshAccess,
        denyCron,
        createDefaultEmail,
        parentResellerId:
          staff && ownerId !== "root" ? ownerId : reseller ? undefined : null,
        featureListId: featureListId || null,
        resources: manual
          ? {
              diskMb: resources.diskMb,
              maxInodes: resources.maxInodes,
              bandwidthMb: resources.bandwidthMb,
              maxFtpAccounts: resources.maxFtpAccounts,
              maxEmails: resources.maxEmails,
              maxEmailQuotaMb: resources.maxEmailQuotaMb,
              maxDatabases: resources.maxDatabases,
              maxSubdomains: resources.maxSubdomains,
              maxParkedDomains: resources.maxParkedDomains,
              maxAddonDomains: resources.maxAddonDomains,
              maxDomains: resources.maxDomains,
              maxHourlyEmail: resources.maxHourlyEmail,
              maxFailPercent: resources.maxFailPercent
            }
          : undefined
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLocalBusy(false);
    }
  };

  const isBusy = busy || localBusy;
  const selectedPlan = hostingPlans.find((p) => p.id === planId);

  const validateStep = (s: number): string | null => {
    if (s === 0) {
      if (!username.trim()) return "Username is required";
      if (!domain.trim()) return "Primary domain is required";
      if (password.length < 8) return "Password must be at least 8 characters";
      if (password !== password2) return "Passwords do not match";
    }
    if (s === 1 && !planId) return "Choose a hosting plan";
    return null;
  };

  const goNext = () => {
    const err = validateStep(step);
    if (err) {
      setError(err);
      return;
    }
    setError("");
    setStep((x) => Math.min(3, x + 1));
  };

  const copyPassword = async () => {
    try {
      await navigator.clipboard.writeText(password);
    } catch {
      /* */
    }
  };

  const steps = [
    { id: 0, label: "Account" },
    { id: 1, label: "Plan" },
    { id: 2, label: "Resources" },
    { id: 3, label: "Settings" }
  ];

  const fmtRes = (v: ResourceValue, unit = "") => {
    if (v == null) return "Unlimited";
    if (unit === "MB" && v >= 1024) return `${(v / 1024).toFixed(v % 1024 === 0 ? 0 : 1)} GB`;
    return `${v}${unit ? ` ${unit}` : ""}`;
  };

  return (
    <form
      className="rp-wizard-form"
      onSubmit={(e) => {
        if (step < 3) {
          e.preventDefault();
          goNext();
          return;
        }
        void submit(e);
      }}
    >
      {error ? <div className="error-banner">{error}</div> : null}

      <div className="rp-steps">
        {steps.map((st) => (
          <div
            key={st.id}
            className={`rp-step${step === st.id ? " is-active" : ""}${
              step > st.id ? " is-done" : ""
            }`}
          >
            <span className="rp-step-num">
              {step > st.id ? "✓" : String(st.id + 1).padStart(2, "0")}
            </span>
            <span>{st.label}</span>
          </div>
        ))}
      </div>

      <div className="rp-wizard">
        <div className="rp-wizard-main">
          {step === 0 ? (
            <div className="rp-wiz-card">
              <h3>Account Information</h3>
              <p>Username, domain, email and password for the new account.</p>
              <div className="rp-wiz-grid">
                <div className="field">
                  <label>Username</label>
                  <input
                    value={username}
                    onChange={(e) =>
                      setUsername(e.target.value.toLowerCase())
                    }
                    required
                    pattern="[a-z0-9][a-z0-9._-]{2,31}"
                    placeholder="jdoe"
                    autoComplete="off"
                  />
                </div>
                <div className="field">
                  <label>Primary Domain</label>
                  <input
                    value={domain}
                    onChange={(e) => {
                      setDomain(e.target.value);
                      suggestUserFromDomain(e.target.value);
                    }}
                    placeholder="example.com"
                    required
                  />
                </div>
                <div className="field full">
                  <label>Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="owner@example.com"
                  />
                </div>
                <div className="field full">
                  <label>Password</label>
                  <div className="rp-pw-group">
                    <input
                      type={showPw ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={8}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw((v) => !v)}
                    >
                      {showPw ? "Hide" : "Show"}
                    </button>
                    <button type="button" onClick={generatePassword}>
                      Generate
                    </button>
                    <button type="button" onClick={() => void copyPassword()}>
                      Copy
                    </button>
                  </div>
                  <div className="rp-pw-meter">
                    <span
                      style={{
                        width: `${strength}%`,
                        background:
                          strength < 40
                            ? "#d64545"
                            : strength < 70
                              ? "#d99116"
                              : "#159a68"
                      }}
                    />
                  </div>
                </div>
                <div className="field full">
                  <label>Confirm password</label>
                  <input
                    type={showPw ? "text" : "password"}
                    value={password2}
                    onChange={(e) => setPassword2(e.target.value)}
                    required
                    minLength={8}
                    autoComplete="new-password"
                  />
                </div>
              </div>
            </div>
          ) : null}

          {step === 1 ? (
            <div className="rp-wiz-card">
              <h3>Select Hosting Plan</h3>
              <p>
                Choose the resource plan for this account. Owner Packages
                (licenses) are managed separately.
              </p>
              {!hostingPlans.length ? (
                <p className="card-meta">
                  No hosting plans yet. Create one under Hosting Plans first.
                </p>
              ) : (
                <div className="rp-plan-cards">
                  {hostingPlans.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      className={`rp-plan-card${
                        planId === p.id ? " is-selected" : ""
                      }`}
                      onClick={() => {
                        setPlanId(p.id);
                        if (!manual) setResources(fromPackage(p));
                      }}
                    >
                      <strong>
                        {p.name}
                        {p.isDefault ? " · Default" : ""}
                      </strong>
                      <ul>
                        {p.maxRamMb && p.maxRamMb > 0 ? (
                          <li>{p.maxRamMb >= 1024 ? `${(p.maxRamMb / 1024).toFixed(0)} GB` : `${p.maxRamMb} MB`} RAM</li>
                        ) : null}
                        <li>
                          {p.diskMb > 0
                            ? p.diskMb >= 1024
                              ? `${(p.diskMb / 1024).toFixed(0)} GB storage`
                              : `${p.diskMb} MB storage`
                            : "Unlimited storage"}
                        </li>
                        <li>
                          {p.maxDomains > 0
                            ? `${p.maxDomains} website(s)`
                            : "Unlimited websites"}
                        </li>
                        <li>
                          {p.maxDatabases > 0
                            ? `${p.maxDatabases} databases`
                            : "Unlimited databases"}
                        </li>
                      </ul>
                    </button>
                  ))}
                </div>
              )}
              <label
                className="rp-check"
                style={{ marginTop: 16 }}
              >
                <input
                  type="checkbox"
                  checked={manual}
                  onChange={(e) => setManual(e.target.checked)}
                />
                <span>
                  Use custom resources
                  <small>
                    Override plan defaults with unlimited or custom limits on
                    the next step.
                  </small>
                </span>
              </label>
            </div>
          ) : null}

          {step === 2 ? (
            <div className="rp-wiz-card">
              <h3>Resources</h3>
              <p>
                {manual
                  ? "Set each limit to plan default, custom, or unlimited."
                  : "Using plan defaults. Enable custom resources on the Plan step to edit."}
              </p>
              <div className="rp-res-group">
                <h4>Storage</h4>
                <div className="rp-res-grid">
                  <ResourceField
                    label="Disk Space (MB)"
                    value={resources.diskMb}
                    disabled={!manual}
                    onChange={(v) => setRes("diskMb", v)}
                  />
                  <ResourceField
                    label="Max Inodes"
                    value={resources.maxInodes}
                    disabled={!manual}
                    onChange={(v) => setRes("maxInodes", v)}
                  />
                </div>
              </div>
              <div className="rp-res-group">
                <h4>Network</h4>
                <div className="rp-res-grid">
                  <ResourceField
                    label="Bandwidth (MB / month)"
                    value={resources.bandwidthMb}
                    disabled={!manual}
                    onChange={(v) => setRes("bandwidthMb", v)}
                  />
                </div>
              </div>
              <div className="rp-res-group">
                <h4>Websites</h4>
                <div className="rp-res-grid">
                  <ResourceField
                    label="Max Domains"
                    value={resources.maxDomains}
                    disabled={!manual}
                    onChange={(v) => setRes("maxDomains", v)}
                  />
                  <ResourceField
                    label="Max Subdomains"
                    value={resources.maxSubdomains}
                    disabled={!manual}
                    onChange={(v) => setRes("maxSubdomains", v)}
                  />
                  <ResourceField
                    label="Parked Domains"
                    value={resources.maxParkedDomains}
                    disabled={!manual}
                    onChange={(v) => setRes("maxParkedDomains", v)}
                  />
                  <ResourceField
                    label="Addon Domains"
                    value={resources.maxAddonDomains}
                    disabled={!manual}
                    onChange={(v) => setRes("maxAddonDomains", v)}
                  />
                </div>
              </div>
              <div className="rp-res-group">
                <h4>Email · Databases · FTP</h4>
                <div className="rp-res-grid">
                  <ResourceField
                    label="Email Accounts"
                    value={resources.maxEmails}
                    disabled={!manual}
                    onChange={(v) => setRes("maxEmails", v)}
                  />
                  <ResourceField
                    label="Mailbox Quota (MB)"
                    value={resources.maxEmailQuotaMb}
                    disabled={!manual}
                    onChange={(v) => setRes("maxEmailQuotaMb", v)}
                  />
                  <ResourceField
                    label="Databases"
                    value={resources.maxDatabases}
                    disabled={!manual}
                    onChange={(v) => setRes("maxDatabases", v)}
                  />
                  <ResourceField
                    label="FTP Accounts"
                    value={resources.maxFtpAccounts}
                    disabled={!manual}
                    onChange={(v) => setRes("maxFtpAccounts", v)}
                  />
                </div>
              </div>
            </div>
          ) : null}

          {step === 3 ? (
            <>
              <div className="rp-wiz-card">
                <h3>Settings</h3>
                <p>Security, environment and ownership options.</p>
                <div className="rp-res-group">
                  <h4>Security</h4>
                  <div className="rp-check-grid">
                    <label className="rp-check">
                      <input
                        type="checkbox"
                        checked={dedicatedIpv4}
                        onChange={(e) => setDedicatedIpv4(e.target.checked)}
                      />
                      <span>Dedicated IPv4</span>
                    </label>
                    <label className="rp-check">
                      <input
                        type="checkbox"
                        checked={dedicatedIpv6}
                        onChange={(e) => setDedicatedIpv6(e.target.checked)}
                      />
                      <span>Dedicated IPv6</span>
                    </label>
                    <label className="rp-check">
                      <input
                        type="checkbox"
                        checked={sshAccess}
                        onChange={(e) => setSshAccess(e.target.checked)}
                      />
                      <span>Shell Access</span>
                    </label>
                    <label className="rp-check">
                      <input
                        type="checkbox"
                        checked={denyCron}
                        onChange={(e) => setDenyCron(e.target.checked)}
                      />
                      <span>Deny Cron</span>
                    </label>
                  </div>
                </div>
                <div className="rp-res-group">
                  <h4>Environment</h4>
                  <div className="rp-wiz-grid">
                    <div className="field">
                      <label>Theme</label>
                      <select
                        value={theme}
                        onChange={(e) => setTheme(e.target.value)}
                      >
                        <option value="default">default</option>
                        <option value="light">light</option>
                        <option value="dark">dark</option>
                        <option value="midnight">midnight</option>
                      </select>
                    </div>
                    <div className="field">
                      <label>Locale</label>
                      <select
                        value={locale}
                        onChange={(e) => setLocale(e.target.value)}
                      >
                        <option value="en">English</option>
                        <option value="es">Español</option>
                        <option value="ar">العربية</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="rp-res-group">
                  <h4>Feature set</h4>
                  <div className="field" style={{ maxWidth: 420 }}>
                    <select
                      value={featureListId}
                      onChange={(e) => setFeatureListId(e.target.value)}
                    >
                      <option value="">Default (all features)</option>
                      {featureLists.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.name}
                          {f.isDefault ? " (default)" : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <details className="rp-res-group">
                  <summary
                    style={{
                      cursor: "pointer",
                      fontWeight: 700,
                      fontSize: 12,
                      color: "var(--muted)",
                      textTransform: "uppercase",
                      letterSpacing: "0.04em"
                    }}
                  >
                    Advanced Settings
                  </summary>
                  <div className="rp-wiz-grid" style={{ marginTop: 12 }}>
                    {staff ? (
                      <div className="field">
                        <label>Owner</label>
                        <select
                          value={ownerId}
                          onChange={(e) => setOwnerId(e.target.value)}
                        >
                          <option value="root">root (this server)</option>
                          {resellers.map((r) => (
                            <option key={r.id} value={r.id}>
                              {r.username}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <div className="field">
                        <label>Owner</label>
                        <input value={currentUser.username} disabled />
                      </div>
                    )}
                    <div className="field" style={{ justifyContent: "center" }}>
                      <label className="rp-check" style={{ marginTop: 22 }}>
                        <input
                          type="checkbox"
                          checked={demoAccount}
                          onChange={(e) => setDemoAccount(e.target.checked)}
                        />
                        <span>Demo account</span>
                      </label>
                    </div>
                    <div className="field full">
                      <label className="rp-check">
                        <input
                          type="checkbox"
                          checked={!createDefaultEmail}
                          onChange={(e) =>
                            setCreateDefaultEmail(!e.target.checked)
                          }
                        />
                        <span>Do not create default email account</span>
                      </label>
                    </div>
                  </div>
                </details>
              </div>
            </>
          ) : null}
        </div>

        <aside className="rp-wiz-summary">
          <h4>Account resource summary</h4>
          <dl>
            <div>
              <dt>Plan</dt>
              <dd>{selectedPlan?.name || "—"}</dd>
            </div>
            <div>
              <dt>Domain</dt>
              <dd>{domain || "—"}</dd>
            </div>
            <div>
              <dt>Username</dt>
              <dd className="mono">{username || "—"}</dd>
            </div>
            <div>
              <dt>Storage</dt>
              <dd>{fmtRes(resources.diskMb, "MB")}</dd>
            </div>
            <div>
              <dt>Bandwidth</dt>
              <dd>{fmtRes(resources.bandwidthMb, "MB")}</dd>
            </div>
            <div>
              <dt>Domains</dt>
              <dd>{fmtRes(resources.maxDomains)}</dd>
            </div>
            <div>
              <dt>Databases</dt>
              <dd>{fmtRes(resources.maxDatabases)}</dd>
            </div>
            <div>
              <dt>Email</dt>
              <dd>{fmtRes(resources.maxEmails)}</dd>
            </div>
          </dl>
        </aside>
      </div>

      <div className="rp-wiz-footer">
        <div className="rp-wiz-footer-meta">
          {domain
            ? `Creating account on ${domain}`
            : "Complete each step to create the account"}
        </div>
        <div className="rp-wiz-footer-actions">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onCancel}
            disabled={isBusy}
          >
            Cancel
          </button>
          {step > 0 ? (
            <button
              type="button"
              className="btn btn-ghost"
              disabled={isBusy}
              onClick={() => {
                setError("");
                setStep((s) => Math.max(0, s - 1));
              }}
            >
              Back
            </button>
          ) : null}
          {step < 3 ? (
            <button
              type="button"
              className="btn btn-primary"
              style={{ width: "auto", marginTop: 0 }}
              disabled={isBusy}
              onClick={goNext}
            >
              Continue
            </button>
          ) : (
            <button
              type="submit"
              className="btn btn-primary"
              style={{ width: "auto", marginTop: 0 }}
              disabled={isBusy}
            >
              {isBusy ? "Creating…" : "Create Account"}
            </button>
          )}
        </div>
      </div>
    </form>
  );
}

export function ResourceField({
  label,
  value,
  disabled,
  onChange
}: {
  label: string;
  value: ResourceValue;
  disabled?: boolean;
  onChange: (v: ResourceValue) => void;
}) {
  const isUnl = value === null;
  return (
    <div className="field">
      {label ? <label>{label}</label> : null}
      <div className="wb-resource-row">
        <select
          disabled={disabled}
          value={isUnl ? "unlimited" : "custom"}
          onChange={(e) => {
            if (e.target.value === "unlimited") onChange(null);
            else onChange(value == null || value === 0 ? 1 : value);
          }}
        >
          <option value="unlimited">Unlimited</option>
          <option value="custom">Custom</option>
        </select>
        {!isUnl ? (
          <input
            type="number"
            min={0}
            disabled={disabled}
            value={value ?? 0}
            onChange={(e) => onChange(Number(e.target.value))}
          />
        ) : null}
      </div>
    </div>
  );
}
